import CDialBase from './Dials'
import { PlaceInNestedAkixiNameSpace } from '../utils/Utils'
import { EaseInOutQuad } from '../../EasingFunctions'

interface IXYCoords {
    flStartX: number
    flStartY: number
    flEndX: number
    flEndY: number
}

/**
 * This class draws a new line statistic graphic.
 * @param {object} pNode The DOM element to attach the dial canvas to.
 */
class CConeGauge extends CDialBase {
    /**
     * Private method.
     *
     * Draws the dial on the canvas.
     *
     * @param {number} [flStopValue] Optional - The value at which the dial ends,
     * defaults to the current dial value.
     * @param {number} [flStartValue] Optional - The value at which the dial
     * starts, defaults to the stop value.
     * @param {number} [flFractionComplete=1.0] Optional - How far through the
     * animation we are, defaults to 100%.
     */
    protected Draw(
        flStopValue: number = this.m_flCurrentValue,
        flStartValue: number = flStopValue,
        flFractionComplete = 1.0
    ): void {
        const p2DContext = this.m_pCanvas.getContext('2d') as CanvasRenderingContext2D

        /*
         * Draw the outer dial elements
         */
        p2DContext.fillStyle = this.m_sBackgroundColour
        p2DContext.fillRect(0, 0, this.m_iWidth, this.m_iHeight)

        const flPadding = this.m_iHeight / 15
        const flAngle = 70 * (Math.PI / 180)
        const flPhi = (Math.PI - flAngle) / 2
        const flVerticalPos = this.m_iHeight - flPadding
        const flPointerStartPos = this.m_iHeight - 2 * flPadding
        const flRadius = this.m_iHeight - 2 * flPadding
        let flFontSize = flRadius

        let flUsableValue = EaseInOutQuad(flFractionComplete) * (flStopValue - flStartValue) + flStartValue
        if (this.m_bReverseDirection) {
            flUsableValue = 1.0 - flUsableValue
        }
        const flPointerLength = flRadius - 2 * flPadding

        const flPointerAngle = flPhi + flUsableValue * flAngle
        const flPointerEndXCoord = this.m_iWidth / 2 - flPointerLength * Math.cos(flPointerAngle)
        const flPointerEndYCoord = flPointerStartPos - flPointerLength * Math.sin(flPointerAngle)

        const flStartPosX = this.m_iWidth / 2 - flRadius * Math.cos(flPhi)
        const flEndPosX = this.m_iWidth / 2 + flRadius * Math.cos(flPhi)
        const flPosY = flVerticalPos - flRadius * Math.sin(flPhi)

        const flProgressLineWidth = flRadius / 100
        const flDotSize = flProgressLineWidth * 1.5

        p2DContext.strokeStyle = this.m_sForegroundColour
        p2DContext.lineCap = 'round'
        p2DContext.lineWidth = flDotSize
        p2DContext.beginPath()
        p2DContext.arc(
            this.m_iWidth / 2,
            flVerticalPos,
            flRadius,
            (3 * Math.PI - flAngle) / 2,
            (3 * Math.PI + flAngle) / 2
        )
        p2DContext.stroke()

        p2DContext.beginPath()
        p2DContext.moveTo(this.m_iWidth / 2, flVerticalPos)
        p2DContext.lineTo(flStartPosX, flPosY)
        p2DContext.moveTo(this.m_iWidth / 2, flVerticalPos)
        p2DContext.lineTo(flEndPosX, flPosY)
        p2DContext.stroke()

        /**
         * Creating a function with closure scope to capture the non-member variables
         * returned to draw this object.
         *
         * @param {number} flPercentage The percentage value to calculate the tick mark for.
         */
        const GetXandYTickMarkCoords = (flPercentage: number): IXYCoords => {
            const flProgressPercentage = flPercentage

            const flDelta = flPhi + flProgressPercentage * flAngle
            const flFirstLineXCoord = this.m_iWidth / 2 - flPointerLength * Math.cos(flDelta)
            const flFirstLineYCoord = flPointerStartPos - flPointerLength * Math.sin(flDelta)

            const flLineLength = flRadius - 3 * flPadding
            const flFirstLineEndXCoord = this.m_iWidth / 2 - flLineLength * Math.cos(flDelta)
            const flFirstLineEndYCoord = flPointerStartPos - flLineLength * Math.sin(flDelta)

            return {
                flStartX: flFirstLineXCoord,
                flStartY: flFirstLineYCoord,
                flEndX: flFirstLineEndXCoord,
                flEndY: flFirstLineEndYCoord
            }
        }

        const StartLineCoords = GetXandYTickMarkCoords(0)
        const QuarterLineCoords = GetXandYTickMarkCoords(0.25)
        const HalfLineCoords = GetXandYTickMarkCoords(0.5)
        const ThreeQuarterLineCoords = GetXandYTickMarkCoords(0.75)
        const EndLineCoords = GetXandYTickMarkCoords(1)

        p2DContext.beginPath()
        p2DContext.moveTo(StartLineCoords.flStartX, StartLineCoords.flStartY)
        p2DContext.lineTo(StartLineCoords.flEndX, StartLineCoords.flEndY)

        p2DContext.moveTo(QuarterLineCoords.flStartX, QuarterLineCoords.flStartY)
        p2DContext.lineTo(QuarterLineCoords.flEndX, QuarterLineCoords.flEndY)

        p2DContext.moveTo(HalfLineCoords.flStartX, HalfLineCoords.flStartY)
        p2DContext.lineTo(HalfLineCoords.flEndX, HalfLineCoords.flEndY)

        p2DContext.moveTo(ThreeQuarterLineCoords.flStartX, ThreeQuarterLineCoords.flStartY)
        p2DContext.lineTo(ThreeQuarterLineCoords.flEndX, ThreeQuarterLineCoords.flEndY)

        p2DContext.moveTo(EndLineCoords.flStartX, EndLineCoords.flStartY)
        p2DContext.lineTo(EndLineCoords.flEndX, EndLineCoords.flEndY)
        p2DContext.stroke()

        p2DContext.strokeStyle = 'red'

        // Progress bar
        p2DContext.beginPath()
        p2DContext.moveTo(this.m_iWidth / 2, flPointerStartPos)
        p2DContext.lineTo(flPointerEndXCoord, flPointerEndYCoord)
        p2DContext.stroke()

        p2DContext.fillStyle = 'red'
        p2DContext.beginPath()
        p2DContext.arc(this.m_iWidth / 2, flPointerStartPos, flDotSize, 0, Math.PI * 2)
        p2DContext.fill()

        // Text bounding box calculation
        const flHeightOfTextAboveCorner = flRadius / 2
        const flTextBottomPositionForDrawing = flVerticalPos - flHeightOfTextAboveCorner
        const flBoundingWidth = 2 * flHeightOfTextAboveCorner * Math.tan(flAngle / 2)
        // We say that we won't allow the text to go above the 1/4 tic mark (plus some padding).
        const flMaxAllowableHeight = flTextBottomPositionForDrawing - QuarterLineCoords.flEndY

        p2DContext.fillStyle = this.m_sForegroundColour
        p2DContext.font = `${flFontSize}px ${this.m_sFontFamily}`
        p2DContext.textAlign = 'center'
        p2DContext.textBaseline = 'alphabetic'
        let pTextDimensions = p2DContext.measureText(this.m_sDisplayValue)
        const flWidthRatio = (flBoundingWidth * 0.9) / pTextDimensions.width
        const flHeightRatio = (flMaxAllowableHeight * 1.2) / flFontSize
        const flRatio = Math.min(flWidthRatio, flHeightRatio)
        flFontSize *= flRatio
        p2DContext.font = `${flFontSize}px ${this.m_sFontFamily}`

        pTextDimensions = p2DContext.measureText(this.m_sDisplayValue)
        p2DContext.fillText(this.m_sDisplayValue, this.m_iWidth / 2, flTextBottomPositionForDrawing)
    }
}

export default CConeGauge
PlaceInNestedAkixiNameSpace('Dials', 'CConeGauge', CConeGauge)
